<template>
	<NuxtLayout
		name="detail"
		title="LifeFit"
		description="Tijdens onze LifeFit lessen werk je aan je mobiliteit, stabiliteit en basisconditie met functionele oefeningen. Een rustige en doordachte training voor iedereen die in beweging wil blijven, op een manier die ondersteunend is aan het dagelijks leven."
		:breadcrumbs
		:button="{
			text: 'Informatie aanvragen',
			to: {
				url: '#aanmelden',
				title: 'Small Group Training informatie aanvragen',
			},
			iconRight: 'fa-envelope',
		}"
	>
		<div class="container max-w-screen-md flex flex-col gap-24 md:gap-36 py-36">
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Wat is LifeFit?
				</AtomTitle>
				<p>
					LifeFit is een laagdrempelige, functionele training die speciaal is ontwikkeld voor mensen die op een rustig tempo willen werken aan hun fitheid. De training richt zich op het verbeteren van mobiliteit, stabiliteit, coördinatie en basisconditie. LifeFit is onderdeel van het Small Group Training lidmaatschap van Fit Hier en biedt een veilige en motiverende manier om in beweging te blijven — of je nu herstellende bent, een tijd niet hebt gesport of gewoon op je eigen tempo wilt trainen.
				</p>
			</div>
		</div>
		<div class="container grid sm:grid-cols-2 gap-8">
			<AtomImage
				src="/img/fitness/small-group-training_02.jpg"
				alt="Banded crabwalk in Small Group Training"
				img-class="rounded aspect-video object-cover"
				:width="1220"
				:height="686"
			/>
			<AtomImage
				src="/img/fitness/connexus-1.jpg"
				alt="Gevarieerde oefeningen in Small Group Training"
				img-class="rounded aspect-video object-cover"
				:width="1220"
				:height="686"
			/>
		</div>
		<div class="container max-w-screen-md flex flex-col gap-24 md:gap-36 py-36">
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Voor wie is deze training geschikt?
				</AtomTitle>
				<p>
					LifeFit is geschikt voor iedereen die in beweging wil komen of blijven met oog voor veiligheid en persoonlijke grenzen. Ideaal voor mensen die herstellen van een blessure, na een langere tijd weer willen opbouwen of bewust willen trainen zonder hoge intensiteit. Onze trainers zorgen voor begeleiding op maat, zodat je altijd binnen je eigen niveau werkt.
				</p>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Wat kun je verwachten?
				</AtomTitle>
				<p>
					Elke training duurt 60 minuten en vindt plaats in een kleine groep van maximaal zes deelnemers. Onder begeleiding van een ervaren trainer werk je aan je mobiliteit, basisconditie en spierversterking op een manier die past bij jouw lichaam.
				</p>
				<p>We starten met een rustige warming-up, gevolgd door een afwisselend circuit met functionele oefeningen. De training wordt afgesloten met een reeks stretches en mobiliteitsoefeningen om het herstel te ondersteunen en soepelheid te bevorderen.</p>
				<p>De trainingen bestaan uit onder andere:</p>
				<MoleculeIconList
					class="md:columns-2"
					:items="[
						'Oefeningen met eigen lichaamsgewicht en lichte gewichten',
						'Stabiliteit en balansoefeningen',
						'Coördinatie- en mobiliteitstraining',
						'Functionele kracht en dagelijkse bewegingen',
					]"
				/>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					De voordelen van LifeFit
				</AtomTitle>
				<MoleculeIconList
					class="md:columns-2"
					:items="[
						'Verbetert mobiliteit, coördinatie en basisconditie',
						'Helpt blessures voorkomen en ondersteunt herstel',
						'Creëert zelfvertrouwen en bewegingsplezier',
						'Kleinschalige setting met persoonlijke aandacht',
						'Geschikt voor elk niveau',
					]"
				/>
			</div>
			<div class="flex justify-center gap-8">
				<AtomButton
					size="lg"
					:variant="ButtonVariant.Green"
					icon-right="fa-envelope"
					text="Informatie aanvragen"
					:to="{
						url: '#aanmelden',
						title: 'Small Group Training informatie aanvragen',
					}"
					@click="trackButtonClick('Page_LifeFit_CTA_Lead')"
				/>
				<AtomButton
					size="lg"
					:variant="ButtonVariant.DarkBorder"
					icon-right="fa-bolt"
					text="Meedoen met een gratis training"
					:to="{
						url: '/small-group-training/aanmelden-proefles',
						title: 'Aanmelden voor een Small Group Training proefles',
					}"
				/>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Waarom trainen bij Fit Hier?
				</AtomTitle>
				<p>
					Bij Fit Hier in Uden train je in een rustige, overzichtelijke omgeving met persoonlijke begeleiding en aandacht voor jouw situatie. LifeFit is speciaal ontwikkeld voor mensen die bewust willen werken aan gezondheid, herstel en fitheid, zonder overprikkeling of prestatiedruk. Onze trainers zorgen ervoor dat jij je op je gemak voelt én met een goed gevoel naar huis gaat.
				</p>
			</div>
			<div class="flex flex-col gap-4">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Informatie aanvragen
				</AtomTitle>
				<p>
					Wil je meer weten over het Small Group Training lidmaatschap? Vraag het informatiepakket aan en je ontvangt binnen enkele minuten alle details over de trainingen, het rooster en de tarieven per mail.
				</p>
			</div>
			<AtomCard
				id="aanmelden"
				class="p-12 scroll-mt-36"
			>
				<OrganismFormLeadSgt />
			</AtomCard>

			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
					class="text-center"
				>
					Ervaringen van leden
				</AtomTitle>
				<OrganismReviewSlideshow :reviews="reviews" />
			</div>

			<div class="flex flex-col gap-16">
				<AtomTitle
					tag="h2"
					size="2xl"
					class="text-center"
				>
					Veelgestelde vragen
				</AtomTitle>
				<OrganismFaq
					:items="lifeFitFaqItems"
				/>
			</div>
		</div>
	</NuxtLayout>
</template>

<script setup lang="ts">
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import reviews from '#fit/core/consts/reviews';
import { lifeFitFaqItems } from '#fit/core/consts/faqItems';

useSeoMeta({
	title: 'LifeFit laagdrempelige functionele fitness Uden | Fit Hier',
	ogTitle: 'LifeFit, laagdrempelige Small Group Training | Fit Hier Uden',
	description: 'Ervaar LifeFit: laagdrempelige functionele fitness in kleine groepen in Uden. Persoonlijke coaching voor veilig, vitaal bewegen. Boek je gratis proefles!',
});

const breadcrumbs: BreadcrumbsType = [
	{
		text: 'Home',
		to: {
			title: 'Home',
			url: '/',
		},
	},
	{
		text: 'Small Group Training',
		to: {
			url: '/small-group-training',
			title: 'Small Group Training',
		},
	},
	{
		text: 'LifeFit',
		to: {
			url: '/small-group-training/lifefit-functionele-begeleide-fitness',
			title: 'LifeFit',
		},
	},
];

const { trackButtonClick } = useEventTracking();
</script>
